import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import { EditableText } from "../../../../../../../components/EditableText/EditableText.tsx";
import { ClickableIcon } from "../../../../../../../components/Icon/ClickableIcon.tsx";
import {
  Icon,
  type IconName,
} from "../../../../../../../components/Icon/Icon.tsx";
import { PanelSection } from "../../../../../components/Panel/PanelSection.tsx";
import {
  ASSET_LAYER_TYPE,
  IMAGE_LAYER_TYPE,
  LINE_LAYER_TYPE,
  SHAPE_LAYER_TYPE,
  TEXT_LAYER_TYPE,
} from "../../../Designer/constants.ts";
import { useLayers } from "../../../Designer/hooks/useLayers.ts";
import { useSelectLayers } from "../../../Designer/hooks/useSelectLayers.ts";
import { designerCanvasStore } from "../../../Designer/stores/designerCanvasStore.ts";
import type { Layer } from "../../../Designer/types.ts";

export const LayersHierarchySection = () => {
  const layers = useLayers();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    designerCanvasStore.reorderLayers(active.id as string, over.id as string);
  };

  const layerIds = layers.map((layer) => layer.id);

  return (
    <PanelSection title="Layers">
      <div className="overflow-y-auto">
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          modifiers={[
            restrictToVerticalAxis,
            restrictToFirstScrollableAncestor,
          ]}
        >
          <SortableContext
            items={layerIds}
            strategy={verticalListSortingStrategy}
          >
            {/* FIXME: We should add multi selection logic with shift and ctrl/cmd keys */}
            {layers.map((layer) => (
              <SortableLayerItem key={layer.id} layer={layer} />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </PanelSection>
  );
};

const SortableLayerItem = ({ layer }: { layer: Layer }) => {
  const { selectedLayerIds } = designerCanvasStore.useState();
  const isSelected = selectedLayerIds.includes(layer.id);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: layer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  let iconName: IconName;
  switch (layer.layerType) {
    case TEXT_LAYER_TYPE:
      iconName = "Type";
      break;
    case IMAGE_LAYER_TYPE:
      iconName = "Images";
      break;
    case SHAPE_LAYER_TYPE:
      iconName = "VectorSquare";
      break;
    case LINE_LAYER_TYPE:
      iconName = "Spline";
      break;
    case ASSET_LAYER_TYPE:
      iconName = "Image";
      break;
  }

  const { selectLayers } = useSelectLayers();

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <LayerItemLayout
        iconName={iconName}
        name={layer.name}
        isSelected={isSelected}
        onClick={() => {
          selectLayers([layer]);
        }}
        onTextEdit={(newValue) => {
          designerCanvasStore.setLayerName(layer.id, newValue);
        }}
        dragHandleProps={listeners}
        rightItems={
          <div className="flex-row gap-100">
            <ClickableIcon
              name={layer.visible ? "Eye" : "EyeClosed"}
              variant="tertiary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                designerCanvasStore.toggleLayerVisibility(layer.id);
              }}
            />
            <ClickableIcon
              name="Trash"
              variant="tertiary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                designerCanvasStore.removeLayer(layer);
              }}
            />
          </div>
        }
      />
    </div>
  );
};

const LayerItemLayout = ({
  iconName,
  name,
  isSelected,
  onClick,
  onTextEdit,
  rightItems,
  dragHandleProps,
}: {
  iconName: IconName;
  name: string;
  isSelected?: boolean;
  onClick?: () => void;
  onTextEdit?: (newValue: string) => void;
  rightItems?: React.ReactNode;
  dragHandleProps?: any;
}) => (
  <div
    className={classNames(
      "group flex-row gap-150 items-center py-100 px-100 rounded-200 cursor-pointer",
      isSelected === undefined
        ? "bg-surface-primary-rest"
        : isSelected
        ? "bg-surface-primary-active"
        : "hover:bg-surface-primary-hover bg-surface-primary-rest",
    )}
    onClick={onClick ?? undefined}
    {...dragHandleProps}
  >
    {/* FIXME: This should be extended to support other layer types such as images */}
    <div className="p-[3px] bg-primary rounded-100 border">
      <Icon name={iconName} size="sm" />
    </div>
    {/* FIXME: Improve layer name to display text type instead */}
    <EditableText
      value={name}
      onSave={(newValue) => onTextEdit?.(newValue)}
      className="flex-fill truncate label-md-default text-primary"
      allowEditOnClick={false}
    />
    {rightItems}
  </div>
);
